import * as React from "react";
import { useId } from "@utdanningsdirektoratet/utils/useId";
import { Color, Icon, SmallIcon } from "@utdanningsdirektoratet/icon";
import { Tooltip } from "@utdanningsdirektoratet/tooltip";
import { cls } from "@utdanningsdirektoratet/utils/cls";
import styles from "./Textarea.module.less";

interface TextareaProps extends React.InputHTMLAttributes<HTMLTextAreaElement> {
  id?: string;
  label?: React.ReactNode | string;
  disabled?: boolean;
  required?: boolean;
  message?: React.ReactNode | string;
  error?: boolean;
  className?: string;
  icon?: SmallIcon | "none";
  placeholder?: string;
  info?: boolean;
  autoGrow?: boolean;
  onChange?: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLTextAreaElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLTextAreaElement>) => void;
  erroralways?: boolean;
  cols?: number;
  rows?: number;
  autoFocus?: boolean;
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (props, forwardedRef) => {
    const id = useId(props.id);
    const [isFocused, setIsFocused] = React.useState(false);
    const ref = React.useRef<HTMLTextAreaElement>(null);

    const {
      disabled,
      required,
      message,
      label,
      error,
      className,
      autoGrow,
      placeholder,
      value,
      info,
      erroralways,
      cols = 20,
      rows = 2,
      onChange,
      onBlur,
      icon = info && !error ? "circle info" : "warning",
      onFocus,
      autoFocus,
      style,
      ...rest
    } = props;

    const resize = (): void => {
      if (!autoGrow || !ref.current) {
        return;
      }
      const htmlTop = window.pageYOffset;
      const bodyTop = document.body.scrollTop;
      ref.current.style.height = "auto";
      ref.current.style.height = `${ref.current.scrollHeight}px`;

      document.documentElement.scrollTop = htmlTop;
      document.body.scrollTop = bodyTop;
    };

    React.useEffect(() => {
      resize();
      setTimeout(() => {
        resize();
      }, 100);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const focus = React.useCallback((): void => {
      if (ref.current) ref.current.focus();
    }, []);

    React.useEffect(() => {
      if (autoFocus) focus();
    }, [focus, autoFocus]);

    React.useEffect(() => {
      resize();
    });

    React.useImperativeHandle(forwardedRef, () => ref.current!, [ref]);

    const onChangeWrapped = (
      event: React.ChangeEvent<HTMLTextAreaElement>,
    ): void => {
      if (onChange) {
        onChange(event);
      }
      resize();
    };

    const onBlurWrapped = (
      event: React.FocusEvent<HTMLTextAreaElement>,
    ): void => {
      if (onBlur) {
        onBlur(event);
      }
      resize();
      setIsFocused(false);
    };

    const onFocusWrapped = (
      event: React.FocusEvent<HTMLTextAreaElement>,
    ): void => {
      if (onFocus) {
        onFocus(event);
      }
      setIsFocused(true);
    };

    const inputClass = cls({
      [styles.textareaInput]: true,
      [styles.textareaInputError]: error,
      [styles.textareaInputInfo]: info,
      [styles.textareaInputDisabled]: disabled,
      [styles.textareaInputAutogrow]: autoGrow,
    });

    const iconClass = cls({
      [styles.textareaIcon]: true,
      [styles.textareaIconVisible]: error || info,
    });
    const labelClass = cls({
      [styles.textareaLabel]: true,
      [styles.textareaLabelPadding]: icon && !label,
      [styles.textareaLabelFocused]: isFocused,
    });

    let iconColor;
    if (info) iconColor = "darkblue" as Color;
    if (error) iconColor = "darkred" as Color;

    return (
      <Tooltip
        style={style}
        className={className}
        message={message}
        disabled={!(error || info)}
        alwaysShow={erroralways === undefined ? error : erroralways}
        align="right"
        alert
      >
        <label htmlFor={id} id={`${id}-label`} className={labelClass}>
          {label}
          {icon ? (
            <Icon
              className={iconClass}
              color={iconColor}
              aria-hidden={error || info ? "false" : "true"}
              icon={icon}
            />
          ) : null}
          <textarea
            {...rest}
            ref={ref}
            required={required}
            aria-required={required}
            disabled={disabled}
            onChange={onChangeWrapped}
            onBlur={onBlurWrapped}
            onFocus={onFocusWrapped}
            aria-invalid={error}
            aria-label={props["aria-label"]}
            aria-labelledby={label ? `${id}-label` : undefined}
            className={inputClass}
            id={id}
            value={value}
            placeholder={placeholder}
            aria-multiline="true"
            cols={cols}
            rows={rows}
          />
        </label>
      </Tooltip>
    );
  },
);

export default Textarea;
